import { useState, useEffect, useCallback } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Space, Tabs, Tooltip, Spin, Alert, Divider, Modal, Form, InputNumber } from 'antd';
import { ReloadOutlined, SettingOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import {
  fetchCircuitBreakers,
  resetCircuitBreaker,
  resetAllCircuitBreakers,
  updateCircuitBreakerConfig,
  fetchCircuitBreakerEvents
} from '../api/health';
import { CircuitBreaker, CircuitBreakerEvent, CircuitBreakerUpdateConfig } from '../types/circuitBreaker';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

function CircuitBreakerManagementPage() {
  // Translation hooks
  const { t: commonT } = useTranslationWithNamespace('common');
  const { t: healthT } = useTranslationWithNamespace('health');

  // State
  const [circuitBreakers, setCircuitBreakers] = useState<CircuitBreaker[]>([]);
  const [circuitBreakerEvents, setCircuitBreakerEvents] = useState<CircuitBreakerEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [eventsLoading, setEventsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [configModalVisible, setConfigModalVisible] = useState<boolean>(false);
  const [selectedCircuitBreaker, setSelectedCircuitBreaker] = useState<CircuitBreaker | null>(null);
  const [eventsModalVisible, setEventsModalVisible] = useState<boolean>(false);
  const [selectedCircuitBreakerName, setSelectedCircuitBreakerName] = useState<string | null>(null);
  const [resetConfirmModalVisible, setResetConfirmModalVisible] = useState<boolean>(false);
  const [resetAllConfirmModalVisible, setResetAllConfirmModalVisible] = useState<boolean>(false);
  const [configForm] = Form.useForm();

  // Fetch circuit breakers
  const fetchData = useCallback(async (bypassCache = false) => {
    setLoading(true);
    setError(null);
    try {
      console.log('CircuitBreakerManagementPage: Fetching circuit breakers');
      const data = await fetchCircuitBreakers(bypassCache);
      setCircuitBreakers(data.circuitBreakers || []);
    } catch (err) {
      console.error('Error fetching circuit breakers:', err);
      setError(healthT('circuitBreakers.fetchError', 'Failed to fetch circuit breakers'));
    } finally {
      setLoading(false);
    }
  }, [healthT]); // Only depend on healthT

  // Fetch circuit breaker events
  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      console.log('CircuitBreakerManagementPage: Fetching circuit breaker events');
      const data = await fetchCircuitBreakerEvents(100);
      setCircuitBreakerEvents(data.events || []);
    } catch (err) {
      console.error('Error fetching circuit breaker events:', err);
    } finally {
      setEventsLoading(false);
    }
  }, []); // No dependencies

  // Initial data fetch - only run once
  useEffect(() => {
    console.log('CircuitBreakerManagementPage: Initial data fetch');
    fetchData(false); // Use cache for initial load
    fetchEvents();

    // No need for cleanup as we only want to fetch once
  }, []); // Empty dependency array to run only once

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // Reset a circuit breaker
  const handleResetCircuitBreaker = async () => {
    if (!selectedCircuitBreaker) return;

    try {
      const operationType = selectedCircuitBreaker.metrics.operationType;
      const operationName = selectedCircuitBreaker.name.split(':')[1];

      await resetCircuitBreaker(operationName, operationType);
      showSuccessNotification(
        healthT('circuitBreakers.resetSuccess', 'Circuit Breaker Reset'),
        healthT('circuitBreakers.resetSuccessMessage', 'Circuit breaker has been reset successfully')
      );

      // Refresh data with bypass cache
      fetchData(true);
      fetchEvents();

      // Close modal
      setResetConfirmModalVisible(false);
    } catch (err) {
      console.error('Error resetting circuit breaker:', err);
      showErrorNotification(
        healthT('circuitBreakers.resetError', 'Reset Failed'),
        healthT('circuitBreakers.resetErrorMessage', 'Failed to reset circuit breaker')
      );
    }
  };

  // Reset all circuit breakers
  const handleResetAllCircuitBreakers = async () => {
    try {
      await resetAllCircuitBreakers();
      showSuccessNotification(
        healthT('circuitBreakers.resetAllSuccess', 'Circuit Breakers Reset'),
        healthT('circuitBreakers.resetAllSuccessMessage', 'All circuit breakers have been reset successfully')
      );

      // Refresh data with bypass cache
      fetchData(true);
      fetchEvents();

      // Close modal
      setResetAllConfirmModalVisible(false);
    } catch (err) {
      console.error('Error resetting all circuit breakers:', err);
      showErrorNotification(
        healthT('circuitBreakers.resetAllError', 'Reset Failed'),
        healthT('circuitBreakers.resetAllErrorMessage', 'Failed to reset all circuit breakers')
      );
    }
  };

  // Open configuration modal
  const openConfigModal = (circuitBreaker: CircuitBreaker) => {
    setSelectedCircuitBreaker(circuitBreaker);

    // Set form values
    configForm.setFieldsValue({
      failureThreshold: circuitBreaker.options.failureThreshold,
      resetTimeout: circuitBreaker.options.resetTimeout / 1000, // Convert ms to seconds for UI
      halfOpenSuccesses: circuitBreaker.options.halfOpenSuccesses,
      autoResetTimeout: circuitBreaker.options.autoResetTimeout ? circuitBreaker.options.autoResetTimeout / 1000 : undefined // Convert ms to seconds for UI
    });

    setConfigModalVisible(true);
  };

  // Close configuration modal
  const closeConfigModal = () => {
    setConfigModalVisible(false);
    setSelectedCircuitBreaker(null);
    configForm.resetFields();
  };

  // Update circuit breaker configuration
  const handleUpdateConfig = async (values: any) => {
    if (!selectedCircuitBreaker) return;

    try {
      const operationType = selectedCircuitBreaker.metrics.operationType;
      const operationName = selectedCircuitBreaker.name.split(':')[1];

      // Convert seconds back to milliseconds for API
      const config: CircuitBreakerUpdateConfig = {
        failureThreshold: values.failureThreshold,
        resetTimeout: values.resetTimeout * 1000,
        halfOpenSuccesses: values.halfOpenSuccesses,
        autoResetTimeout: values.autoResetTimeout ? values.autoResetTimeout * 1000 : undefined
      };

      await updateCircuitBreakerConfig(operationName, operationType, config);
      showSuccessNotification(
        healthT('circuitBreakers.updateSuccess', 'Configuration Updated'),
        healthT('circuitBreakers.updateSuccessMessage', 'Circuit breaker configuration has been updated successfully')
      );

      // Refresh data with bypass cache
      fetchData(true);

      // Close modal
      closeConfigModal();
    } catch (err) {
      console.error('Error updating circuit breaker configuration:', err);
      showErrorNotification(
        healthT('circuitBreakers.updateError', 'Update Failed'),
        healthT('circuitBreakers.updateErrorMessage', 'Failed to update circuit breaker configuration')
      );
    }
  };

  // Open events modal
  const openEventsModal = (circuitBreakerName: string) => {
    setSelectedCircuitBreakerName(circuitBreakerName);
    setEventsModalVisible(true);
  };

  // Close events modal
  const closeEventsModal = () => {
    setEventsModalVisible(false);
    setSelectedCircuitBreakerName(null);
  };

  // Get state tag color
  const getStateTagColor = (state: string) => {
    switch (state) {
      case 'CLOSED':
        return 'green';
      case 'OPEN':
        return 'red';
      case 'HALF_OPEN':
        return 'orange';
      default:
        return 'default';
    }
  };

  // Get event type tag color
  const getEventTypeTagColor = (eventType: string) => {
    switch (eventType) {
      case 'CLOSED':
        return 'green';
      case 'OPEN':
        return 'red';
      case 'HALF_OPEN':
        return 'orange';
      case 'RESET':
        return 'blue';
      default:
        return 'default';
    }
  };

  // Filter circuit breakers based on active tab
  const filteredCircuitBreakers = circuitBreakers.filter(cb => {
    if (activeTab === 'all') return true;
    return cb.metrics.operationType === activeTab;
  });

  // Filter events for selected circuit breaker
  const filteredEvents = circuitBreakerEvents.filter(event => {
    if (!selectedCircuitBreakerName) return true;
    return event.circuit_breaker_name === selectedCircuitBreakerName;
  });

  // Table columns for circuit breakers
  const columns = [
    {
      title: healthT('circuitBreakers.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => {
        const parts = text.split(':');
        return (
          <Tooltip title={text}>
            <span>{parts[1] || text}</span>
          </Tooltip>
        );
      }
    },
    {
      title: healthT('circuitBreakers.type', 'Type'),
      dataIndex: ['metrics', 'operationType'],
      key: 'type',
      render: (text: string) => <Tag>{text}</Tag>
    },
    {
      title: healthT('circuitBreakers.state', 'State'),
      dataIndex: 'state',
      key: 'state',
      render: (text: string) => <Tag color={getStateTagColor(text)}>{text}</Tag>
    },
    {
      title: healthT('circuitBreakers.failureThreshold', 'Failure Threshold'),
      dataIndex: ['options', 'failureThreshold'],
      key: 'failureThreshold'
    },
    {
      title: healthT('circuitBreakers.resetTimeout', 'Reset Timeout'),
      dataIndex: ['options', 'resetTimeout'],
      key: 'resetTimeout',
      render: (value: number) => `${value / 1000}s`
    },
    {
      title: healthT('circuitBreakers.halfOpenSuccesses', 'Half-Open Successes'),
      dataIndex: ['options', 'halfOpenSuccesses'],
      key: 'halfOpenSuccesses'
    },
    {
      title: healthT('circuitBreakers.autoResetTimeout', 'Auto-Reset Timeout'),
      dataIndex: ['options', 'autoResetTimeout'],
      key: 'autoResetTimeout',
      render: (value: number) => value ? `${value / 1000}s` : '-'
    },
    {
      title: healthT('circuitBreakers.failureCount', 'Failure Count'),
      dataIndex: ['metrics', 'failedCalls'],
      key: 'failureCount'
    },
    {
      title: healthT('circuitBreakers.actions', 'Actions'),
      key: 'actions',
      render: (_: any, record: CircuitBreaker) => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => openConfigModal(record)}
            size="small"
          >
            {healthT('circuitBreakers.configure', 'Configure')}
          </Button>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => openEventsModal(record.name)}
            size="small"
          >
            {healthT('circuitBreakers.events', 'Events')}
          </Button>
          <Button
            danger
            onClick={() => {
              setSelectedCircuitBreaker(record);
              setResetConfirmModalVisible(true);
            }}
            size="small"
          >
            {healthT('circuitBreakers.reset', 'Reset')}
          </Button>
        </Space>
      )
    }
  ];

  // Table columns for events
  const eventColumns = [
    {
      title: healthT('circuitBreakers.eventTime', 'Time'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: healthT('circuitBreakers.eventType', 'Event Type'),
      dataIndex: 'event_type',
      key: 'event_type',
      render: (text: string) => <Tag color={getEventTypeTagColor(text)}>{text}</Tag>
    },
    {
      title: healthT('circuitBreakers.circuitBreaker', 'Circuit Breaker'),
      dataIndex: 'circuit_breaker_name',
      key: 'circuit_breaker_name',
      render: (text: string) => {
        const parts = text.split(':');
        return (
          <Tooltip title={text}>
            <span>{parts[1] || text}</span>
          </Tooltip>
        );
      }
    },
    {
      title: healthT('circuitBreakers.operationType', 'Operation Type'),
      dataIndex: 'operation_type',
      key: 'operation_type',
      render: (text: string) => <Tag>{text}</Tag>
    },
    {
      title: healthT('circuitBreakers.details', 'Details'),
      dataIndex: 'details',
      key: 'details',
      render: (details: any) => (
        <Tooltip title={JSON.stringify(details, null, 2)}>
          <Button size="small">{healthT('circuitBreakers.viewDetails', 'View')}</Button>
        </Tooltip>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{healthT('circuitBreakers.title', 'Circuit Breaker Management')}</Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchData(true); // Use bypassCache=true for manual refresh
                fetchEvents();
              }}
              loading={loading}
            >
              {commonT('actions.refresh')}
            </Button>
            <Button
              danger
              onClick={() => setResetAllConfirmModalVisible(true)}
            >
              {healthT('circuitBreakers.resetAll', 'Reset All Circuit Breakers')}
            </Button>
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert
          message={healthT('circuitBreakers.error', 'Error')}
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card style={{ marginTop: 16 }}>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab={healthT('circuitBreakers.allTypes', 'All Types')} key="all">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCircuitBreakers}
                rowKey="name"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>
          <TabPane tab={healthT('circuitBreakers.queueType', 'Queue')} key="QUEUE">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCircuitBreakers}
                rowKey="name"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>
          <TabPane tab={healthT('circuitBreakers.databaseType', 'Database')} key="DATABASE">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCircuitBreakers}
                rowKey="name"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>
          <TabPane tab={healthT('circuitBreakers.blockchainType', 'Blockchain')} key="BLOCKCHAIN">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCircuitBreakers}
                rowKey="name"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>
          <TabPane tab={healthT('circuitBreakers.externalApiType', 'External API')} key="EXTERNAL_API">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCircuitBreakers}
                rowKey="name"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>
          <TabPane tab={healthT('circuitBreakers.callbackType', 'Callback')} key="CALLBACK">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCircuitBreakers}
                rowKey="name"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>
          <TabPane tab={healthT('circuitBreakers.notificationType', 'Notification')} key="NOTIFICATION">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCircuitBreakers}
                rowKey="name"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      <Divider />

      <Card title={healthT('circuitBreakers.eventsTitle', 'Circuit Breaker Events')}>
        {eventsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={eventColumns}
            dataSource={circuitBreakerEvents}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1000 }}
          />
        )}
      </Card>

      {/* Configuration Modal */}
      <Modal
        title={healthT('circuitBreakers.configureTitle', 'Configure Circuit Breaker')}
        open={configModalVisible}
        onCancel={closeConfigModal}
        footer={null}
      >
        {selectedCircuitBreaker && (
          <Form
            form={configForm}
            layout="vertical"
            onFinish={handleUpdateConfig}
          >
            <Form.Item
              label={healthT('circuitBreakers.circuitBreakerName', 'Circuit Breaker')}
            >
              <Text strong>{selectedCircuitBreaker.name}</Text>
            </Form.Item>

            <Form.Item
              label={healthT('circuitBreakers.failureThreshold', 'Failure Threshold')}
              name="failureThreshold"
              rules={[
                { required: true, message: healthT('circuitBreakers.failureThresholdRequired', 'Please enter failure threshold') },
                { type: 'number', min: 1, message: healthT('circuitBreakers.failureThresholdMin', 'Threshold must be at least 1') }
              ]}
              tooltip={healthT('circuitBreakers.failureThresholdHelp', 'Number of failures before the circuit breaker opens')}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={healthT('circuitBreakers.resetTimeout', 'Reset Timeout (seconds)')}
              name="resetTimeout"
              rules={[
                { required: true, message: healthT('circuitBreakers.resetTimeoutRequired', 'Please enter reset timeout') },
                { type: 'number', min: 1, message: healthT('circuitBreakers.resetTimeoutMin', 'Timeout must be at least 1 second') }
              ]}
              tooltip={healthT('circuitBreakers.resetTimeoutHelp', 'Time in seconds before attempting to close the circuit')}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={healthT('circuitBreakers.halfOpenSuccesses', 'Half-Open Successes')}
              name="halfOpenSuccesses"
              rules={[
                { required: true, message: healthT('circuitBreakers.halfOpenSuccessesRequired', 'Please enter half-open successes') },
                { type: 'number', min: 1, message: healthT('circuitBreakers.halfOpenSuccessesMin', 'Must be at least 1') }
              ]}
              tooltip={healthT('circuitBreakers.halfOpenSuccessesHelp', 'Number of successful operations in half-open state to close the circuit')}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={healthT('circuitBreakers.autoResetTimeout', 'Auto-Reset Timeout (seconds)')}
              name="autoResetTimeout"
              tooltip={healthT('circuitBreakers.autoResetTimeoutHelp', 'Time in seconds after which the circuit breaker will automatically reset (optional)')}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={closeConfigModal}>
                  {commonT('actions.cancel')}
                </Button>
                <Button type="primary" htmlType="submit">
                  {commonT('actions.save')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Events Modal */}
      <Modal
        title={healthT('circuitBreakers.eventsForTitle', 'Events for Circuit Breaker')}
        open={eventsModalVisible}
        onCancel={closeEventsModal}
        footer={[
          <Button key="close" onClick={closeEventsModal}>
            {commonT('actions.close')}
          </Button>
        ]}
        width={800}
      >
        {eventsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={eventColumns.filter(col => col.dataIndex !== 'circuit_breaker_name')}
            dataSource={filteredEvents}
            rowKey="_id"
            pagination={{ pageSize: 5 }}
            scroll={{ x: 600 }}
          />
        )}
      </Modal>

      {/* Reset Confirm Modal */}
      <Modal
        title={healthT('circuitBreakers.resetConfirmTitle', 'Reset Circuit Breaker')}
        open={resetConfirmModalVisible}
        onCancel={() => setResetConfirmModalVisible(false)}
        onOk={handleResetCircuitBreaker}
        okText={healthT('circuitBreakers.reset', 'Reset')}
        okButtonProps={{ danger: true }}
      >
        <p>{healthT('circuitBreakers.resetConfirmMessage', 'Are you sure you want to reset this circuit breaker? This will close the circuit and reset all failure counts.')}</p>
        {selectedCircuitBreaker && (
          <p><strong>{healthT('circuitBreakers.circuitBreaker', 'Circuit Breaker')}:</strong> {selectedCircuitBreaker.name}</p>
        )}
      </Modal>

      {/* Reset All Confirm Modal */}
      <Modal
        title={healthT('circuitBreakers.resetAllConfirmTitle', 'Reset All Circuit Breakers')}
        open={resetAllConfirmModalVisible}
        onCancel={() => setResetAllConfirmModalVisible(false)}
        onOk={handleResetAllCircuitBreakers}
        okText={healthT('circuitBreakers.resetAll', 'Reset All')}
        okButtonProps={{ danger: true }}
      >
        <p>{healthT('circuitBreakers.resetAllConfirmMessage', 'Are you sure you want to reset ALL circuit breakers? This will close all circuits and reset all failure counts.')}</p>
      </Modal>
    </div>
  );
};

export default CircuitBreakerManagementPage;
